import { ifAll, TGetValueFn } from "@components/smart/FieldInfo";
import { IFormGroupDef, IFormLineItemsDef } from "@components/smart/smartFormGroup/SmartFormGroup";
import BindingContext, { createPath } from "@odata/BindingContext";
import {
    IPrTaxpayerDeclarationEntity,
    PrAnnualTaxSettlementRequestEntity,
    PrTaxpayerDeclarationChildEntity,
    PrTaxpayerDeclarationEntity
} from "@odata/GeneratedEntityTypes";
import { PrTaxDiscountTypeCode } from "@odata/GeneratedEnums";
import { TFieldsDefinition } from "@pages/PageUtils";
import { hasAdditionalTaxPayerDeclarationPath } from "@pages/payroll/annualTaxSettlementRequest/AnnualTaxSettlementRequest.def";
import { MasterCheckboxType } from "@pages/payroll/Eldp/MonthCheckboxSelector";
import SmartMonthCheckboxSelector from "@pages/payroll/Eldp/SmartMonthCheckboxSelector";
import {
    getDefinitions as taxPayerDeclarationDef,
    TaxPayerDeclarationGroupIds
} from "@pages/payroll/taxpayerDeclaration/TaxpayerDeclaration.def";
import { getTogglePropPath, hasDisabilityDiscount } from "@pages/payroll/taxpayerDeclaration/TaxpayerDeclaration.utils";
import { isDefined, isNotDefined, isObjectEmpty } from "@utils/general";
import React from "react";

import { IAppContext } from "../../../contexts/appContext/AppContext.types";
import { FieldType } from "../../../enums";
import { FormStorage } from "../../../views/formView/FormStorage";

const navigationFromAnnualTaxSettlementRequest = PrAnnualTaxSettlementRequestEntity.YearlyTaxpayerDeclaration;

function getCorrectPath(path: string): string {
    if (isNotDefined(path)) {
        return path;
    }
    if (BindingContext.isLocalContextPath(path)) {
        return path;
    }
    return `${navigationFromAnnualTaxSettlementRequest}/${path}`;
}

const hasAdditionalTaxPayerDeclaration: TGetValueFn<boolean> = ({ storage }): boolean => {
    return storage.getValueByPath(hasAdditionalTaxPayerDeclarationPath);
};

const EXCLUDED_GROUPS = [TaxPayerDeclarationGroupIds.Validity, TaxPayerDeclarationGroupIds.Employee];
export const DISCOUNT_GROUPS_WITH_VALIDITY: string[] = [
    "disability",
    PrTaxDiscountTypeCode.DiscountForZTP_PHolder
];

export function discountValidityPath(discountTypeCode: string): string {
    return BindingContext.localContext(discountTypeCode + "Validity");
}


export function getAdditionalTaxDeclarationGroups(context: IAppContext): IFormGroupDef[] {
    return taxPayerDeclarationDef(context).form.groups
        .filter(group => !EXCLUDED_GROUPS.includes(group.id as TaxPayerDeclarationGroupIds))
        .map(group => {
            let lineItems: IFormLineItemsDef = undefined;
            if (group.lineItems) {
                lineItems = {
                    ...group.lineItems,
                    collection: getCorrectPath(group.lineItems?.collection),
                };

                if (group.id === PrTaxpayerDeclarationEntity.Children) {
                    lineItems.stretchLastField = true;
                    lineItems.renderAsFastEntryForReadOnly = true;
                    // Disabled, otherwise month picker is not aligned properly
                    lineItems.useLabelWrapping = false;
                    lineItems.columns = [
                        ...lineItems.columns,
                        { id: PrTaxpayerDeclarationChildEntity.IncludedMonths }
                    ];
                }
            }

            if (DISCOUNT_GROUPS_WITH_VALIDITY.includes(group.id)) {
                const validityField = { id: discountValidityPath(group.id) };
                if (group.rows.length === 0) {
                    group.rows.push([validityField]);
                } else {
                    group.rows[group.rows.length - 1]?.push(validityField);
                }
            }

            return {
                ...group,
                isVisible: ifAll(hasAdditionalTaxPayerDeclaration, group.isVisible),
                lineItems,
                rows: group.rows?.map((row => row?.map(field => ({
                    ...field,
                    id: getCorrectPath(field.id)
                })) ?? [])) ?? []
            };
        });
}

const getAllUsedRowIds = (context: IAppContext): string[] => {
    return getAdditionalTaxDeclarationGroups(context).reduce((acc: string[], group: IFormGroupDef) => {
        if (group.togglePropPath) {
            acc.push(group.togglePropPath);
        }
        if (!isObjectEmpty(group.lineItems)) {
            const prefix = group.lineItems.collection;
            group.lineItems.columns.forEach(c => acc.push(`${prefix}/${c.id}`));
        }
        group.rows.forEach(row => {
            row.forEach(field => {
                acc.push(field.id);
            });
        });
        return acc;
    }, []);
};

export function getAdditionalTaxDeclarationFieldsDef(context: IAppContext): TFieldsDefinition {
    const declarationFieldsDef = taxPayerDeclarationDef(context).form.fieldDefinition;

    const additionalDeclarationDefs: TFieldsDefinition = {};
    const allRows = getAllUsedRowIds(context);

    Object.entries(declarationFieldsDef).forEach(def => {
        const [key, value] = def;
        const correctPath = getCorrectPath(key);
        if (!allRows.includes(correctPath)) {
            return;
        }
        additionalDeclarationDefs[correctPath] = {
            ...value,
            isVisible: ifAll(value.isVisible, hasAdditionalTaxPayerDeclaration)
        };
    });

    for (const discountId of DISCOUNT_GROUPS_WITH_VALIDITY) {
        additionalDeclarationDefs[discountValidityPath(discountId)] = {
            type: FieldType.Custom,
            defaultValue: "0".repeat(12),
            render: (args) => {
                return (
                    <SmartMonthCheckboxSelector {...args.props} {...args.events}
                                                masterCheckboxType={MasterCheckboxType.All}/>);
            },
            isVisible: args => {
                const path = discountId === "disability" ? hasDisabilityDiscount : getTogglePropPath(discountId as PrTaxDiscountTypeCode);
                return !!args.storage.getValueByPath(path);
            },
        };
    }

    additionalDeclarationDefs[getCorrectPath(createPath(PrTaxpayerDeclarationEntity.Children, PrTaxpayerDeclarationChildEntity.IncludedMonths))] = {
        type: FieldType.Custom,
        defaultValue: "0".repeat(12),
        render: (args) => {
            return (
                <SmartMonthCheckboxSelector {...args.props} {...args.events} bindingContext={args.bindingContext}
                                            masterCheckboxType={MasterCheckboxType.All}/>);
        },
    };

    const childDisabilityDiscountPath = getCorrectPath(createPath(
        PrTaxpayerDeclarationEntity.Children,
        PrTaxpayerDeclarationChildEntity.IsDisabilityDiscount
    ));
    additionalDeclarationDefs[childDisabilityDiscountPath] = {
        ...additionalDeclarationDefs[childDisabilityDiscountPath],
        customFieldStyle: {
            marginRight: "13px"
        }
    };

    return additionalDeclarationDefs;
}

export function setDiscountValidityAfterLoad(entity: IPrTaxpayerDeclarationEntity, storage: FormStorage): void {
    for (const discountId of DISCOUNT_GROUPS_WITH_VALIDITY) {
        const validityPath = discountValidityPath(discountId);
        const matchingDiscount = entity.Discounts.find(d => {
            if (discountId === "disability") {
                return d.DiscountTypeCode === PrTaxDiscountTypeCode.BasicDisabilityDiscount || d.DiscountTypeCode === PrTaxDiscountTypeCode.ExtendedDisabilityDiscount;
            }
            return d.DiscountTypeCode === discountId;
        });
        if (isDefined(matchingDiscount)) {
            storage.setValueByPath(validityPath, matchingDiscount.IncludedMonths);
        }
    }
}

export function setDiscountValidityBeforeSave(entity: IPrTaxpayerDeclarationEntity, storage: FormStorage): void {
    for (const discountId of DISCOUNT_GROUPS_WITH_VALIDITY) {
        const validityPath = discountValidityPath(discountId);
        const includedMonths = storage.getValueByPath(validityPath) as string;
        const matchingDiscount = entity.Discounts.find(d => {
            if (discountId === "disability") {
                return d.DiscountTypeCode === PrTaxDiscountTypeCode.BasicDisabilityDiscount || d.DiscountTypeCode === PrTaxDiscountTypeCode.ExtendedDisabilityDiscount;
            }
            return d.DiscountTypeCode === discountId;
        });
        if (isDefined(matchingDiscount)) {
            matchingDiscount.IncludedMonths = includedMonths;
        }
    }
}
